import React, { useState, useEffect } from 'react';
import * as triviaApi from '../../api/trivia';
import LakersTriviaChallengeModal from './LakersTriviaChallengeModal';

// Algorithm definitions with their hyperparameters
const ALGORITHMS = {
  leiden: {
    name: 'Leiden',
    description: 'Graph-based community detection. Best for finding natural groupings without specifying cluster count.',
    params: {
      resolution: { type: 'slider', min: 0.1, max: 3.0, step: 0.1, default: 1.0, label: 'Resolution', help: 'Higher = more clusters' },
      n_neighbors: { type: 'slider', min: 5, max: 100, step: 5, default: 30, label: 'Neighbors', help: 'k-NN graph connectivity' },
    },
    badge: 'Recommended',
    badgeColor: 'bg-green-100 text-green-800',
  },
  hdbscan: {
    name: 'HDBSCAN',
    description: 'Density-based clustering with automatic outlier detection. Great for noisy data.',
    params: {
      min_cluster_size: { type: 'slider', min: 5, max: 100, step: 5, default: 15, label: 'Min Cluster Size', help: 'Minimum points per cluster' },
      min_samples: { type: 'slider', min: 1, max: 50, step: 1, default: 5, label: 'Min Samples', help: 'Core point threshold' },
      cluster_selection_epsilon: { type: 'slider', min: 0, max: 1, step: 0.05, default: 0, label: 'Epsilon', help: 'Distance threshold' },
    },
    badge: 'Handles Outliers',
    badgeColor: 'bg-purple-100 text-purple-800',
  },
  kmeans: {
    name: 'K-Means',
    description: 'Classic centroid-based clustering. Fast and simple, requires specifying cluster count.',
    params: {
      n_clusters: { type: 'slider', min: 2, max: 50, step: 1, default: 10, label: 'Clusters', help: 'Number of clusters to create' },
      n_init: { type: 'slider', min: 1, max: 20, step: 1, default: 10, label: 'Initializations', help: 'Random starts (more = stable)' },
    },
    badge: 'Fast',
    badgeColor: 'bg-blue-100 text-blue-800',
  },
  spectral: {
    name: 'Spectral',
    description: 'Graph-based clustering via eigenvectors. Good for non-convex cluster shapes.',
    params: {
      n_clusters: { type: 'slider', min: 2, max: 50, step: 1, default: 10, label: 'Clusters', help: 'Number of clusters' },
      n_neighbors: { type: 'slider', min: 5, max: 50, step: 5, default: 10, label: 'Neighbors', help: 'Graph connectivity' },
      affinity: { type: 'select', options: ['nearest_neighbors', 'rbf'], default: 'nearest_neighbors', label: 'Affinity', help: 'Similarity measure' },
    },
    badge: 'Complex Shapes',
    badgeColor: 'bg-amber-100 text-amber-800',
  },
  agglomerative: {
    name: 'Agglomerative',
    description: 'Hierarchical bottom-up clustering. Builds a tree of clusters.',
    params: {
      n_clusters: { type: 'slider', min: 2, max: 100, step: 1, default: 10, label: 'Clusters', help: 'Number of clusters' },
      linkage: { type: 'select', options: ['ward', 'complete', 'average', 'single'], default: 'ward', label: 'Linkage', help: 'Merge criterion' },
    },
    badge: 'Hierarchical',
    badgeColor: 'bg-indigo-100 text-indigo-800',
  },
  gmm: {
    name: 'Gaussian Mixture',
    description: 'Probabilistic soft clustering. Assigns membership probabilities, not hard labels.',
    params: {
      n_components: { type: 'slider', min: 2, max: 50, step: 1, default: 10, label: 'Components', help: 'Number of Gaussians' },
      covariance_type: { type: 'select', options: ['full', 'tied', 'diag', 'spherical'], default: 'full', label: 'Covariance', help: 'Cluster shape' },
    },
    badge: 'Soft Clustering',
    badgeColor: 'bg-pink-100 text-pink-800',
  },
};

const DIM_REDUCTION_METHODS = {
  none: {
    name: 'None',
    description: 'Use embeddings directly without reduction.',
    params: {},
  },
  pca: {
    name: 'PCA',
    description: 'Fast linear reduction. Removes noise, preserves variance.',
    params: {
      n_components: { type: 'slider', min: 10, max: 200, step: 10, default: 50, label: 'Components', help: 'Target dimensions' },
      whiten: { type: 'checkbox', default: false, label: 'Whiten', help: 'Decorrelate components' },
    },
  },
  umap: {
    name: 'UMAP',
    description: 'Non-linear reduction. Preserves cluster structure.',
    params: {
      n_components: { type: 'slider', min: 2, max: 50, step: 1, default: 30, label: 'Components', help: 'Target dimensions' },
      n_neighbors: { type: 'slider', min: 5, max: 100, step: 5, default: 15, label: 'Neighbors', help: 'Local neighborhood' },
      min_dist: { type: 'slider', min: 0, max: 0.99, step: 0.05, default: 0.1, label: 'Min Distance', help: 'Point spacing' },
    },
  },
};

// Param input components
function SliderParam({ param, value, onChange }) {
  return (
    <div>
      <div className="flex justify-between mb-1">
        <label className="text-sm font-medium text-gray-700">{param.label}</label>
        <span className="text-sm font-mono text-gray-900">{value}</span>
      </div>
      <input
        type="range"
        min={param.min}
        max={param.max}
        step={param.step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-violet-600"
      />
      <p className="text-xs text-gray-500 mt-1">{param.help}</p>
    </div>
  );
}

function SelectParam({ param, value, onChange }) {
  return (
    <div>
      <label className="text-sm font-medium text-gray-700 block mb-1">{param.label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
      >
        {param.options.map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
      <p className="text-xs text-gray-500 mt-1">{param.help}</p>
    </div>
  );
}

function CheckboxParam({ param, value, onChange }) {
  return (
    <div className="flex items-center gap-3">
      <input
        type="checkbox"
        checked={value}
        onChange={(e) => onChange(e.target.checked)}
        className="w-4 h-4 text-violet-600 rounded focus:ring-violet-500"
      />
      <div>
        <label className="text-sm font-medium text-gray-700">{param.label}</label>
        <p className="text-xs text-gray-500">{param.help}</p>
      </div>
    </div>
  );
}

export default function AdvancedTopicConfig({
  onConfigChange,
  initialConfig = {},
  showTriviaGate = true,
}) {
  const [isUnlocked, setIsUnlocked] = useState(!showTriviaGate);
  const [showTrivia, setShowTrivia] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);

  const [algorithm, setAlgorithm] = useState(initialConfig.algorithm || 'leiden');
  const [algorithmParams, setAlgorithmParams] = useState(initialConfig.algorithm_params || {});
  const [dimReduction, setDimReduction] = useState(initialConfig.dim_reduction || 'none');
  const [dimReductionParams, setDimReductionParams] = useState(initialConfig.dim_reduction_params || {});
  const [computeMetrics, setComputeMetrics] = useState(initialConfig.compute_metrics !== false);

  // Check unlock status on mount
  useEffect(() => {
    if (!showTriviaGate) {
      setCheckingStatus(false);
      return;
    }

    const checkStatus = async () => {
      try {
        const res = await triviaApi.getTriviaStatus();
        const data = res.data || res;
        setIsUnlocked(data.unlocked);
      } catch (err) {
        console.error('Failed to check trivia status:', err);
      } finally {
        setCheckingStatus(false);
      }
    };
    checkStatus();
  }, [showTriviaGate]);

  // Initialize params with defaults when algorithm changes
  useEffect(() => {
    const algo = ALGORITHMS[algorithm];
    if (algo) {
      const defaults = {};
      Object.entries(algo.params).forEach(([key, param]) => {
        defaults[key] = algorithmParams[key] ?? param.default;
      });
      setAlgorithmParams(defaults);
    }
  }, [algorithm]);

  // Initialize dim reduction params when method changes
  useEffect(() => {
    const method = DIM_REDUCTION_METHODS[dimReduction];
    if (method) {
      const defaults = {};
      Object.entries(method.params).forEach(([key, param]) => {
        defaults[key] = dimReductionParams[key] ?? param.default;
      });
      setDimReductionParams(defaults);
    }
  }, [dimReduction]);

  // Notify parent of config changes
  useEffect(() => {
    onConfigChange?.({
      algorithm,
      algorithm_params: algorithmParams,
      dim_reduction: dimReduction === 'none' ? null : dimReduction,
      dim_reduction_params: dimReduction === 'none' ? null : dimReductionParams,
      compute_metrics: computeMetrics,
    });
  }, [algorithm, algorithmParams, dimReduction, dimReductionParams, computeMetrics, onConfigChange]);

  const handleUnlock = () => {
    setIsUnlocked(true);
    setShowTrivia(false);
  };

  const updateAlgorithmParam = (key, value) => {
    setAlgorithmParams((prev) => ({ ...prev, [key]: value }));
  };

  const updateDimReductionParam = (key, value) => {
    setDimReductionParams((prev) => ({ ...prev, [key]: value }));
  };

  if (checkingStatus) {
    return (
      <div className="p-6 text-center text-gray-500">
        <div className="animate-spin w-6 h-6 border-2 border-violet-600 border-t-transparent rounded-full mx-auto mb-2" />
        Checking access...
      </div>
    );
  }

  if (!isUnlocked) {
    return (
      <>
        <div className="p-6 text-center">
          <div className="text-5xl mb-4">🔒</div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">Advanced Mode Locked</h3>
          <p className="text-gray-600 mb-4">
            Prove you're a true Lakers fan to unlock full control over clustering algorithms,
            dimension reduction, and hyperparameters.
          </p>
          <button
            onClick={() => setShowTrivia(true)}
            className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-semibold hover:from-purple-700 hover:to-indigo-700 transition shadow-md"
          >
            🏀 Take the Lakers Challenge
          </button>
        </div>
        {showTrivia && (
          <LakersTriviaChallengeModal
            onClose={() => setShowTrivia(false)}
            onUnlock={handleUnlock}
          />
        )}
      </>
    );
  }

  const currentAlgo = ALGORITHMS[algorithm];
  const currentDimReduce = DIM_REDUCTION_METHODS[dimReduction];

  return (
    <div className="space-y-6">
      {/* Unlocked badge */}
      <div className="flex items-center gap-2 text-sm text-purple-700 bg-purple-50 border border-purple-200 rounded-lg px-4 py-2">
        <span className="text-lg">🏆</span>
        <span className="font-medium">Advanced Mode Unlocked</span>
        <span className="text-purple-500">| Full control enabled</span>
      </div>

      {/* Algorithm Selection */}
      <div>
        <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <span className="w-6 h-6 bg-violet-100 rounded-full flex items-center justify-center text-violet-600 text-xs font-bold">1</span>
          Clustering Algorithm
        </h4>
        <div className="grid grid-cols-2 gap-2">
          {Object.entries(ALGORITHMS).map(([key, algo]) => (
            <button
              key={key}
              onClick={() => setAlgorithm(key)}
              className={`p-3 rounded-xl border-2 text-left transition-all ${
                algorithm === key
                  ? 'border-violet-500 bg-violet-50'
                  : 'border-gray-200 hover:border-violet-300'
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <span className="font-semibold text-gray-900">{algo.name}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${algo.badgeColor}`}>
                  {algo.badge}
                </span>
              </div>
              <p className="text-xs text-gray-600 line-clamp-2">{algo.description}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Algorithm Parameters */}
      {currentAlgo && Object.keys(currentAlgo.params).length > 0 && (
        <div className="bg-gray-50 rounded-xl p-4">
          <h5 className="text-sm font-semibold text-gray-700 mb-3">{currentAlgo.name} Parameters</h5>
          <div className="space-y-4">
            {Object.entries(currentAlgo.params).map(([key, param]) => {
              const value = algorithmParams[key] ?? param.default;
              if (param.type === 'slider') {
                return <SliderParam key={key} param={param} value={value} onChange={(v) => updateAlgorithmParam(key, v)} />;
              } else if (param.type === 'select') {
                return <SelectParam key={key} param={param} value={value} onChange={(v) => updateAlgorithmParam(key, v)} />;
              } else if (param.type === 'checkbox') {
                return <CheckboxParam key={key} param={param} value={value} onChange={(v) => updateAlgorithmParam(key, v)} />;
              }
              return null;
            })}
          </div>
        </div>
      )}

      {/* Dimension Reduction */}
      <div>
        <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <span className="w-6 h-6 bg-violet-100 rounded-full flex items-center justify-center text-violet-600 text-xs font-bold">2</span>
          Dimension Reduction (Optional)
        </h4>
        <div className="flex gap-2">
          {Object.entries(DIM_REDUCTION_METHODS).map(([key, method]) => (
            <button
              key={key}
              onClick={() => setDimReduction(key)}
              className={`flex-1 p-3 rounded-xl border-2 text-center transition-all ${
                dimReduction === key
                  ? 'border-violet-500 bg-violet-50'
                  : 'border-gray-200 hover:border-violet-300'
              }`}
            >
              <span className="font-semibold text-gray-900">{method.name}</span>
              <p className="text-xs text-gray-600 mt-1">{method.description}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Dimension Reduction Parameters */}
      {currentDimReduce && Object.keys(currentDimReduce.params).length > 0 && (
        <div className="bg-gray-50 rounded-xl p-4">
          <h5 className="text-sm font-semibold text-gray-700 mb-3">{currentDimReduce.name} Parameters</h5>
          <div className="space-y-4">
            {Object.entries(currentDimReduce.params).map(([key, param]) => {
              const value = dimReductionParams[key] ?? param.default;
              if (param.type === 'slider') {
                return <SliderParam key={key} param={param} value={value} onChange={(v) => updateDimReductionParam(key, v)} />;
              } else if (param.type === 'select') {
                return <SelectParam key={key} param={param} value={value} onChange={(v) => updateDimReductionParam(key, v)} />;
              } else if (param.type === 'checkbox') {
                return <CheckboxParam key={key} param={param} value={value} onChange={(v) => updateDimReductionParam(key, v)} />;
              }
              return null;
            })}
          </div>
        </div>
      )}

      {/* Additional Options */}
      <div>
        <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <span className="w-6 h-6 bg-violet-100 rounded-full flex items-center justify-center text-violet-600 text-xs font-bold">3</span>
          Options
        </h4>
        <div className="bg-gray-50 rounded-xl p-4">
          <CheckboxParam
            param={{
              label: 'Compute Quality Metrics',
              help: 'Calculate silhouette, Calinski-Harabasz, and Davies-Bouldin scores',
            }}
            value={computeMetrics}
            onChange={setComputeMetrics}
          />
        </div>
      </div>
    </div>
  );
}
